/**
 * TransactionApprovalForm Component
 * Dialog-based form for approving QC-verified transactions.
 *
 * Features:
 * - Price per KG input field
 * - Shows calculated total payment
 * - Notes textarea (500 char limit)
 * - Submit button with loading state
 * - Form validation with Zod
 *
 * @author SAUBHAGYA Development Team
 * @since Epic 6
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Loader2, AlertCircle, CheckCircle2, IndianRupee } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { useApproveTransaction } from '@/hooks/usePayments';
import { TransactionDTO } from '@/services/manufacturing/manufacturingApi';
import { QCGradeBadge } from '../badges/QCGradeBadge';

// Form validation schema
const approvalSchema = z.object({
  pricePerKg: z
    .number()
    .min(0.01, 'Price must be greater than 0')
    .max(10000, 'Price seems too high'),
  notes: z
    .string()
    .max(500, 'Notes cannot exceed 500 characters')
    .optional(),
});

type ApprovalFormValues = z.infer<typeof approvalSchema>;

interface TransactionApprovalFormProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Transaction to approve */
  transaction: TransactionDTO | null;
  /** Callback when approval is successful */
  onSuccess?: (transaction: TransactionDTO) => void;
}

/**
 * Get base price suggestion based on grade
 */
function getBasePriceByGrade(grade?: string): number {
  switch (grade) {
    case 'A':
      return 5.0; // Premium grade
    case 'B':
      return 4.0; // Standard grade
    case 'C':
      return 3.0; // Below standard
    case 'D':
      return 2.0; // Low grade
    default:
      return 4.0; // Default
  }
}

export function TransactionApprovalForm({
  open,
  onOpenChange,
  transaction,
  onSuccess,
}: TransactionApprovalFormProps) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Approval mutation
  const approveTransaction = useApproveTransaction();

  // Form setup
  const form = useForm<ApprovalFormValues>({
    resolver: zodResolver(approvalSchema),
    defaultValues: {
      pricePerKg: 4.0,
      notes: '',
    },
  });

  // Watch price for total calculation
  const pricePerKg = form.watch('pricePerKg');
  const totalPayment = transaction ? pricePerKg * transaction.quantityKg : 0;

  // Reset form when transaction changes
  useEffect(() => {
    if (transaction && open) {
      const suggestedPrice = getBasePriceByGrade(transaction.qc?.grade);
      form.reset({
        pricePerKg: suggestedPrice,
        notes: '',
      });
      setError(null);
    }
  }, [transaction, open, form]);

  // Handle form submission
  const onSubmit = async (values: ApprovalFormValues) => {
    if (!transaction) return;

    setError(null);

    try {
      const result = await approveTransaction.mutateAsync({
        transactionId: transaction.id,
        data: {
          pricePerKg: values.pricePerKg,
          notes: values.notes || undefined,
        },
      });

      // Close the modal first
      onOpenChange(false);

      // Navigate to payment detail screen with transaction ID
      // The PaymentListPage will auto-open the modal for this transaction
      navigate(
        `/manufacturing/payments?transactionId=${transaction.id}&autoOpen=true`
      );

      // Call success callback
      onSuccess?.(result);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to approve transaction';
      setError(errorMessage);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Approve Transaction</DialogTitle>
          <DialogDescription>
            {transaction
              ? `Approve and set price for batch ${transaction.batchCode}`
              : 'Select a transaction to approve'}
          </DialogDescription>
        </DialogHeader>

        {transaction && (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6 py-6"
            >
              {/* Error Alert */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Transaction Info */}
              <div className="rounded-lg border p-4 space-y-2 bg-muted/30">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Batch Code</span>
                  <span className="font-mono font-medium">
                    {transaction.batchCode}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Quantity</span>
                  <span className="font-medium">
                    {transaction.quantityKg.toLocaleString('en-IN', { minimumFractionDigits: 2 })} kg
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Material Type</span>
                  <span className="font-medium">
                    {transaction.rawMaterialType || transaction.rawMaterialTypeName || '—'}
                  </span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span className="text-muted-foreground">QC Grade</span>
                  {transaction.qc?.grade ? (
                    <QCGradeBadge grade={transaction.qc.grade as any} size="sm" />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Gaushala</span>
                  <span className="font-medium">
                    {transaction.gaushala?.name || transaction.gaushalaName || '—'}
                  </span>
                </div>
              </div>

              {/* Price Per KG */}
              <FormField
                control={form.control}
                name="pricePerKg"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price per KG (INR)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <IndianRupee className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="number"
                          min={0.01}
                          step={0.01}
                          placeholder="Enter price"
                          className="pl-9"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value) || 0)
                          }
                        />
                      </div>
                    </FormControl>
                    <FormDescription>
                      Suggested price based on Grade {transaction.qc?.grade || 'B'}: INR {getBasePriceByGrade(transaction.qc?.grade).toFixed(2)}/kg
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Total Payment Preview */}
              <div className="rounded-lg border-2 border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Payment
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {transaction.quantityKg.toLocaleString('en-IN', { minimumFractionDigits: 2 })} kg x INR {pricePerKg.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">
                      INR {totalPayment.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Add any approval notes or comments..."
                        className="min-h-[80px] resize-none"
                        maxLength={500}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {(field.value?.length || 0)}/500 characters
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Submit Button */}
              <DialogFooter className="pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={approveTransaction.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={approveTransaction.isPending}>
                  {approveTransaction.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Approve Transaction
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TransactionApprovalForm;
